// frontend/src/components/CuttingPlan/CuttingPlanList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cuttingPlansAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiEye } from 'react-icons/fi';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './CuttingPlanList.css';

const CuttingPlanList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    loadPlans();
  }, [currentPage, itemsPerPage]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      const response = await cuttingPlansAPI.getAll(queryParams.toString());
      
      if (response.data.success) {
        setPlans(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.totalItems);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading cutting plans:', err);
      setError(t('messages.error_load', 'Failed to load cutting plans'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/cutting-plans/new');
  };

  const handleViewPlan = (plan) => {
    navigate(`/cutting-plans/${plan.id}`);
  };

  const columns = [
    {
      header: t('cutting_plans.plan_id', 'Plan ID'),
      field: 'plan_internal_id',
      className: 'code-column'
    },
    {
      header: t('cutting_plans.notes_1', 'Notes 1'),
      field: 'notes_1'
    },
    {
      header: t('cutting_plans.notes_2', 'Notes 2'),
      field: 'notes_2'
    },
    {
      header: t('cutting_plans.planning_group', 'Planning Group'),
      field: 'planning_group'
    },
    {
      header: t('cutting_plans.order_count', 'Orders'),
      field: 'order_count',
      className: 'text-center'
    },
    {
      header: t('created_at', 'Created'),
      field: 'created_at',
      render: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      header: t('actions', 'Actions'),
      field: 'actions',
      sortable: false,
      render: (row) => (
        <div className="action-buttons">
          <button 
            onClick={() => handleViewPlan(row)}
            className="btn-action btn-view"
            title={t('view', 'View')}
          >
            <FiEye size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="cutting-plan-list">
      <div className="page-header">
        <div>
          <h2>{t('cutting_plans.title', 'Cutting Plans')}</h2>
          <p className="page-subtitle">{t('cutting_plans.subtitle', 'Manage fabric cutting plans')}</p>
        </div>
        <button onClick={handleCreateNew} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t('cutting_plans.create', 'Create Cutting Plan')}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={plans}
        loading={loading}
        error={error}
        onRetry={loadPlans}
        emptyMessage={t('no_data', 'No cutting plans found')}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
};

export default CuttingPlanList;